import { Card } from "@fanduel/og-fe-library";
import { useCallback, useMemo, useState } from "react";
import ErrorComponent from "@/components/empty/ErrorComponent";
import { viewOptions } from "@/constants/horseRacing";
import { Hr_PastTrack } from "@/graphql/gql/graphql";
import { LetterFilter } from "../trackListControls";
import TrackListItems from "./TrackListItems";
import styles from "./trackList.module.scss";

interface TrackListProps {
  view: string | undefined;
  usaTracks: Record<string, Hr_PastTrack[]>;
  nonUsaTracks: Record<string, Hr_PastTrack[]>;
  searchTerm: string | undefined;
}
const TrackList = ({
  view,
  usaTracks,
  nonUsaTracks,
  searchTerm,
}: TrackListProps) => {
  const [filterByLetter, setFilterByLetter] = useState<string | null>(null);

  const getFilteredTrackKeys = useCallback(
    (tracksByLocation: Record<string, Hr_PastTrack[]>) => {
      if (!filterByLetter) {
        return Object.keys(tracksByLocation);
      }
      return Object.keys(tracksByLocation).filter((key) =>
        key.toLocaleUpperCase().startsWith(filterByLetter.toLocaleUpperCase())
      );
    },
    [filterByLetter]
  );

  const usList = useMemo(
    () =>
      !view || [viewOptions.ALL, viewOptions.US].includes(view)
        ? getFilteredTrackKeys(usaTracks)
        : [],
    [view, usaTracks, getFilteredTrackKeys]
  );
  const otherList = useMemo(
    () =>
      !view || [viewOptions.ALL, viewOptions.INTERNATIONAL].includes(view)
        ? getFilteredTrackKeys(nonUsaTracks)
        : [],
    [view, nonUsaTracks, getFilteredTrackKeys]
  );

  const showEmptyState = usList.length + otherList.length === 0;

  return (
    <Card variant="elevation" elevation="mid" className={styles.container}>
      <LetterFilter
        setFilterByLetter={setFilterByLetter}
        filterByLetter={filterByLetter}
      />
      {showEmptyState ? (
        <Card
          variant="elevation"
          elevation="mid"
          className={styles.errorContainer}
        >
          <ErrorComponent
            headline={
              searchTerm
                ? `Oops! No results for ‘${searchTerm}’.`
                : "No Tracks Available."
            }
            subtext={
              searchTerm
                ? "Please try a different track name."
                : "Please check back later."
            }
            showCta
          />
        </Card>
      ) : (
        ""
      )}
      <>
        {usList.map((state) => (
          <TrackListItems
            key={state}
            isUs
            tracks={usaTracks[state]}
            title={state}
          />
        ))}
      </>
      <>
        {otherList.map((country) => (
          <TrackListItems
            key={country}
            tracks={nonUsaTracks[country]}
            title={country}
          />
        ))}
      </>
    </Card>
  );
};

export default TrackList;
